import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const SufficiencyIcon = ({ sufficient }) =>
  sufficient ? (
    <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 14 }} />
  ) : (
    <CancelIcon sx={{ color: '#f44336', fontSize: 14 }} />
  );

const fmt$ = (val) => (val != null ? `$${val.toFixed(2)}` : '—');
const fmtE = (val) => (val != null ? `${val.toFixed(1)}` : '—');
// Commented out by Dhirendra on 2026-05-07 to show date time format
//const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

// Added by Dhirendra on 2026-05-07 to show date time format
const fmtDate = (
  d,
  {
    showTime = true,
    usePacific = false,
  } = {}
) => {
  if (!d) return '—';

  const date = new Date(d);

  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(showTime && {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }),
    ...(usePacific && { timeZone: 'America/Los_Angeles' }),
  };

  return new Intl.DateTimeFormat(undefined, options).format(date);
};


export default function SessionSummaryTable({ evaluations = [], onSelectSession }) {
  if (!evaluations.length) return null;

  const sorted = [...evaluations].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );
  
  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          Session Evaluations
        </Typography>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead
                sx={{
                  backgroundColor: (theme) => theme.palette.grey[100],
                  '& th': {
                    fontWeight: 600,
                    backgroundColor: (theme) => theme.palette.grey[100],
                  },
                }}
              >
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>VIN</TableCell>
                <TableCell align="right">
                  <Tooltip title="Energy delivered by the real (Control) session. All strategies are constrained to deliver this amount.">
                    <span>Actual kWh</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="|Predicted departure − Actual departure| in minutes. Lower is better.">
                    <span>Depart Err (min)</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Sched $</TableCell>
                <TableCell align="right">Sched kWh</TableCell>
                <TableCell align="right">Actual $</TableCell>
                <TableCell align="right">Dumb $</TableCell>
                <TableCell align="right">Oracle $</TableCell>
                <TableCell align="right">Oracle kWh</TableCell>
                <TableCell align="right">Pred $</TableCell>
                <TableCell align="right">Pred kWh</TableCell>
                <TableCell align="center">Suff.</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((ev) => {
                const s = ev.strategies || {};
                // Fallback: old evaluations stored a single "control" key
                const controlScheduled = s.control_scheduled || s.control || null;
                const controlActual = s.control_actual || null;
                return (
                  <TableRow
                    key={ev._id || ev.session_id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => onSelectSession && onSelectSession(ev)}
                  >
                    <TableCell>{fmtDate(ev.arrival_time)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                      {ev.vin || '—'}
                    </TableCell>
                    <TableCell align="right">{fmtE(ev.total_energy_kwh)}</TableCell>
                    <TableCell align="right">
                      {ev.prediction_accuracy_minutes != null
                        ? ev.prediction_accuracy_minutes.toFixed(0)
                        : '—'}
                    </TableCell>
                    <TableCell align="right">{fmt$(controlScheduled?.cost_usd)}</TableCell>
                    <TableCell align="right">{fmtE(controlScheduled?.total_energy_kwh)}</TableCell>
                    <TableCell align="right">{fmt$(controlActual?.cost_usd)}</TableCell>
                    <TableCell align="right">{fmt$(s.dumb?.cost_usd)}</TableCell>
                    <TableCell align="right">{fmt$(s.oracle?.cost_usd)}</TableCell>
                    <TableCell align="right">{fmtE(s.oracle?.total_energy_kwh)}</TableCell>
                    <TableCell align="right">{fmt$(s.prediction_only?.cost_usd)}</TableCell>
                    <TableCell align="right">{fmtE(s.prediction_only?.total_energy_kwh)}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <SufficiencyIcon sufficient={controlScheduled?.charge_sufficient} />
                      <SufficiencyIcon sufficient={controlActual?.charge_sufficient} />
                      <SufficiencyIcon sufficient={s.dumb?.charge_sufficient} />
                      <SufficiencyIcon sufficient={s.oracle?.charge_sufficient} />
                      <SufficiencyIcon sufficient={s.prediction_only?.charge_sufficient} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}
